/*
 * IBKÜL — PDP variant speed patch (Minimog product-info).
 *
 * 1. Changing SIZE no longer re-renders the gallery when the media set is
 *    unchanged. (Minimog always empties and refills the gallery and resets
 *    the slider.)
 * 2. Changing COLOR swaps the gallery immediately on click, using the
 *    variant → media mapping already on the page (no server round trip).
 *    The first image of the new color loads with high priority.
 * 3. Hovering / touching a swatch prefetches the section response and the
 *    first image of that color, so price/stock also update faster on click.
 * 4. The variant ID in the Add to Cart form and the button state (Sold Out)
 *    are updated as soon as an option changes, so a quick Add to Cart click
 *    still adds the correct variant.
 * 5. Size (and other non-color) cross-outs are updated for the new color
 *    right away, re-applied twice shortly after (Swatch King re-renders the
 *    swatches after each click) and checked again once the server response
 *    for the last selected variant is applied (stock refreshed from that
 *    response), so they match the Add to Cart button state, also with fast
 *    clicks. No observers: the number of writes per click is fixed.
 * 6. Every variant ID input of the main product (including the installment
 *    form and a form-less input that Minimog never updates) follows the
 *    selected variant. linked-product-swap.js reads the first of those
 *    inputs to decide which sizes to cross out.
 *
 * Linked colors: while linked-product-swap.js shows a linked product, the
 * patch stands down completely (no form, button, cross-out or gallery
 * changes), because the variant data on the page belongs to the main
 * product only.
 *
 * Does not modify Minimog theme files; it only patches the <product-info>
 * prototype once that element is defined. Load right after product-info.js.
 */
(function () {
  'use strict';

  // Run only on product detail pages. The product URL can also have a
  // prefix: /collections/{handle}/products/{handle} (opened from a
  // collection) or /{locale}/products/{handle} (markets / languages).
  if (!/(^|\/)products\/[^/]+/.test(window.location.pathname)) return;

  // Check the installed version in the console: window.__ibkPdpSpeedVersion
  window.__ibkPdpSpeedVersion = '2026-10-24.1';

  const PREFETCH_TTL = 60 * 1000;
  const PREFETCH_MAX = 40;
  const HOVER_DELAY = 80;
  const SWATCH_SELECTOR =
    'variant-swatch-king [id^="swatch-option"] li.swatch-view-item';

  /* ---------- Helpers ---------- */

  function normalize(value) {
    return String(value == null ? '' : value).trim();
  }

  // Media set key for a variant.
  // null = variant group images disabled; '' = variant has no mapping.
  function mediaKeyFor(pi, variantId) {
    const data = pi.variantGroupImagesData;
    if (!pi.enableVariantGroupImages || !data || !data.enable) return null;

    const entry = (data.mapping || []).find(
      item => Number(item.id) === Number(variantId)
    );
    return entry && Array.isArray(entry.media) ? entry.media.join(',') : '';
  }

  function findMediaNode(pi, mediaId) {
    return Array.from(pi.initialMedias || []).find(
      node => node.dataset.mediaId === mediaId
    );
  }

  // Load the first images of the new color right away, with high priority.
  function prioritizeMedia(pi, key) {
    key.split(',').slice(0, 2).forEach((mediaId, index) => {
      const img = findMediaNode(pi, mediaId)?.querySelector('img');
      if (!img) return;
      img.loading = 'eager';
      img.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
    });
  }

  function preloadFirstImage(pi, variantId) {
    const key = mediaKeyFor(pi, variantId);
    if (!key) return;

    const img = findMediaNode(pi, key.split(',')[0])?.querySelector('img');
    if (!img || img.__ibkPreloaded) return;
    img.__ibkPreloaded = true;

    const pre = new Image();
    if (img.sizes) pre.sizes = img.sizes;
    if (img.srcset) pre.srcset = img.srcset;
    pre.src = img.currentSrc || img.src;
  }

  // True when the gallery already shows the same images as this media set.
  // Compared as a set, not in order: on some products one size of a color
  // lists the same images in a different order (or twice), which would
  // otherwise rebuild the gallery on a plain size change. Checked against
  // the real DOM because linked-product-swap.js also edits the gallery.
  function galleryShows(pi, key) {
    const list = pi.productMedia?.querySelector('.m-media-gallery__list');
    if (!list) return false;

    const available = new Set(
      Array.from(pi.initialMedias || []).map(node => node.dataset.mediaId)
    );
    const wanted = new Set(key.split(',').filter(id => available.has(id)));
    const shown = new Set(
      Array.from(list.children)
        .filter(node => node.dataset && node.dataset.mediaType === 'image')
        .map(node => node.dataset.mediaId)
    );

    if (!wanted.size || wanted.size !== shown.size) return false;
    for (const id of wanted) {
      if (!shown.has(id)) return false;
    }
    return true;
  }

  // Variant list: { id, key: "Black / Solid / XS", available }.
  // `available` is undefined when that data is not on the page.
  // The list belongs to the product that rendered the page section;
  // `__ibkVariantsFor` records which product ID that was.
  function getVariants(pi) {
    if (pi.__ibkVariants) return pi.__ibkVariants;
    pi.__ibkVariantsFor = pi.getAttribute('data-product-id');

    let list = [];
    try {
      const custom = JSON.parse(pi.productMedia?.dataset.ibkulVariants || 'null');
      if (Array.isArray(custom)) {
        list = custom.map(v => ({
          id: Number(v.id),
          key: (v.options || []).map(normalize).join(' / '),
          available: typeof v.available === 'boolean' ? v.available : undefined
        }));
      }
    } catch (e) { /* fall back to ShopifyAnalytics below */ }

    if (!list.length) {
      const product = window.ShopifyAnalytics?.meta?.product;
      if (product && String(product.id) === String(pi.productId)) {
        list = (product.variants || []).map(v => ({
          id: Number(v.id),
          key: normalize(v.public_title),
          available: undefined
        }));
      }
    }

    pi.__ibkVariants = list;
    return list;
  }

  // Refresh stock from a fresh section response (Minimog's variantChange
  // event carries the parsed HTML), so availability never relies only on
  // the data from page load. Only for the same product.
  function refreshAvailability(pi, html) {
    const variants = getVariants(pi);
    if (!html || !variants.length) return;

    const source = html.querySelector('product-info');
    if (source && source.getAttribute('data-product-id') !== pi.__ibkVariantsFor) return;

    try {
      const fresh = JSON.parse(
        html.querySelector('[data-ibkul-variants]')?.getAttribute('data-ibkul-variants') || 'null'
      );
      if (!Array.isArray(fresh)) return;

      const byId = new Map(fresh.map(v => [Number(v.id), v.available]));
      variants.forEach(v => {
        const available = byId.get(v.id);
        if (typeof available === 'boolean') v.available = available;
      });
    } catch (e) { /* keep the current data */ }
  }

  // `picker` = the variant-picker to read from. After a server response
  // Minimog keeps the previous picker (hidden) on the page for 500 ms, so
  // during fast clicks the event can come from a different picker than
  // pi.variantPicker. Always read from the picker that fired the event.
  function getOptionGroups(pi, picker = pi.variantPicker) {
    return picker ? Array.from(picker.querySelectorAll('.m-product-option')) : [];
  }

  // Selected value name per option; `override` = { index, value } for hover.
  function getSelectedNames(pi, override, picker) {
    const names = getOptionGroups(pi, picker).map((group, index) => {
      if (override && override.index === index) return normalize(override.value);

      const select = group.querySelector('select');
      if (select) return normalize(select.value);

      const checked = group.querySelector('input[type="radio"]:checked');
      return checked ? normalize(checked.value) : null;
    });

    return names.length && names.every(Boolean) ? names : null;
  }

  // True when the page still shows the product our variant list belongs to
  // and every selected value exists in that list. False while
  // linked-product-swap.js has swapped in a linked product (another product
  // ID, or a color that is not an option of this product): the patch then
  // stands down and leaves everything to that script and Minimog.
  function isOwnSelection(pi, names) {
    const variants = getVariants(pi);
    if (!names || !variants.length) return false;
    if (pi.getAttribute('data-product-id') !== pi.__ibkVariantsFor) return false;

    return names.every((name, index) =>
      variants.some(v => v.key.split(' / ')[index] === name)
    );
  }

  function findVariant(pi, names) {
    if (!names) return null;
    const key = names.join(' / ');
    return getVariants(pi).find(v => v.key === key) || null;
  }

  function findVariantId(pi, names) {
    const match = findVariant(pi, names);
    return match ? match.id : null;
  }

  // Must match Minimog's buildRequestUrlWithParams exactly.
  function buildSectionUrl(pi, override) {
    const ids = [];

    for (const [index, group] of getOptionGroups(pi).entries()) {
      let field = null;
      const select = group.querySelector('select');

      if (select) {
        const options = Array.from(select.options);
        field =
          override && override.index === index
            ? options.find(o => normalize(o.value) === normalize(override.value))
            : options.find(o => o.selected && !o.disabled) ||
              options.find(o => !o.disabled);
      } else {
        const inputs = Array.from(group.querySelectorAll('input[type="radio"]'));
        field =
          override && override.index === index
            ? inputs.find(i => normalize(i.value) === normalize(override.value))
            : inputs.find(i => i.checked) || inputs[0];
      }

      if (!field || !field.dataset.optionValueId) return null;
      if (!ids.includes(field.dataset.optionValueId)) {
        ids.push(field.dataset.optionValueId);
      }
    }

    if (!ids.length) return null;
    return `${pi.dataset.url}?section_id=${pi.sectionId}&option_values=${ids.join(',')}`;
  }

  /* ---------- Prefetch cache ---------- */

  const cache = new Map();

  function getCached(url) {
    const hit = cache.get(url);
    if (!hit) return null;
    if (Date.now() - hit.time > PREFETCH_TTL) {
      cache.delete(url);
      return null;
    }
    return hit.promise;
  }

  function prefetch(url) {
    if (!url || getCached(url)) return;

    const promise = fetch(url).then(response => {
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return response.text();
    });
    promise.catch(() => cache.delete(url));

    cache.set(url, { time: Date.now(), promise });
    if (cache.size > PREFETCH_MAX) cache.delete(cache.keys().next().value);
  }

  // Set every input[name="id"] inside this product-info to the new variant.
  // Inputs inside the Selly widget are left alone.
  function syncVariantInputs(pi, variantId) {
    const value = variantId == null ? '' : String(variantId);

    pi.querySelectorAll('input[name="id"]').forEach(input => {
      if (input.closest('.tdf_rec')) return;
      if (input.value !== value) input.value = value;
    });
  }

  /* ---------- Patch <product-info> ---------- */

  function patch(ProductInfo) {
    const P = ProductInfo.prototype;
    if (P.__ibkSpeedPatched) return;
    P.__ibkSpeedPatched = true;

    // 1. Skip the gallery re-render when it already shows this media set.
    //    Unmapped variants (e.g. linked products) always go to Minimog.
    const originalUpdateMedia = P.updateMedia;
    const shouldSkipMedia = safe(function (pi, variant) {
      const key = variant ? mediaKeyFor(pi, variant.id) : null;
      if (!key) return false;
      if (galleryShows(pi, key)) return true;
      prioritizeMedia(pi, key);
      return false;
    });
    P.updateMedia = function (variant) {
      if (shouldSkipMedia(this, variant)) return;
      return originalUpdateMedia.apply(this, arguments);
    };

    // 6. Keep every variant ID input of the main product in sync.
    //    Minimog only updates #product-form-{sectionId} and
    //    #product-form-installment-{sectionId}; in this theme the installment
    //    form is #product-form-installment, and there is also a form-less
    //    input. Both kept the variant from page load, and
    //    linked-product-swap.js reads the first input[name="id"] on the page,
    //    so it crossed out sizes based on that old variant.
    const originalUpdateVariantInputs = P.updateVariantInputs;
    const safeSync = safe(syncVariantInputs);
    P.updateVariantInputs = function (variantId) {
      const result = originalUpdateVariantInputs.apply(this, arguments);
      safeSync(this, variantId);
      return result;
    };

    // 3. Use the prefetched response when available.
    const originalRender = P.renderProductInfo;
    const safeGetCached = safe(getCached);
    P.renderProductInfo = function (args) {
      const cached = args && safeGetCached(args.requestUrl);
      if (!cached) return originalRender.call(this, args);

      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;

      cached
        .then(
          text => {
            if (controller.signal.aborted) return;
            this.pendingRequestUrl = null;
            args.callback(new DOMParser().parseFromString(text, 'text/html'));
            if (args.targetId) {
              document.querySelector(`#${CSS.escape(args.targetId)}`)?.focus();
            }
          },
          () => {
            // Prefetch failed: fetch again the normal way.
            if (controller.signal.aborted) return;
            cache.delete(args.requestUrl);
            originalRender.call(this, args);
          }
        )
        .catch(error => console.error(error));
    };

    // Build the variant list now, before any linked-product swap can change
    // the product ID on the element.
    document.querySelectorAll('product-info').forEach(pi => getVariants(pi));
  }

  // Cross out sold-out values of every non-color option (e.g. Size) for the
  // new selection right away. Swatch King / linked-product-swap.js only do
  // this after the server responds, so until then the sizes would still show
  // the stock of the previous color. The color option is left untouched.
  function updateSwatchAvailability(pi, names) {
    const variants = getVariants(pi);
    if (!names || !variants.length) return;
    if (variants.some(v => typeof v.available !== 'boolean')) return;

    const byKey = new Map(variants.map(v => [v.key, v]));

    pi.querySelectorAll('variant-swatch-king [id^="swatch-option"]').forEach(option => {
      if (/^colou?r$/i.test(normalize(option.getAttribute('option-name')))) return;

      const index = parseInt(option.id.replace('swatch-option', ''), 10) - 1;
      if (!(index >= 0) || index >= names.length) return;

      option.querySelectorAll('li.swatch-view-item').forEach(li => {
        const value = normalize(li.getAttribute('orig-value'));
        if (!value) return;

        const combo = names.slice();
        combo[index] = value;
        const match = byKey.get(combo.join(' / '));
        const soldOut = !match || !match.available;

        // Only write when something changes, so observers in other
        // scripts are not triggered for nothing.
        if (li.classList.contains('swatch-item-unavailable') !== soldOut) {
          li.classList.toggle('swatch-item-unavailable', soldOut);
        }
        if (li.classList.contains('swatch-view-item--soldout') !== soldOut) {
          li.classList.toggle('swatch-view-item--soldout', soldOut);
        }
        const label = soldOut ? `${value} - Sold Out` : value;
        if (li.getAttribute('aria-label') !== label) li.setAttribute('aria-label', label);
      });
    });
  }

  /* ---------- Correct Swatch King re-renders ---------- */

  // Swatch King re-renders the size list (new <li> elements) right after
  // every click. When the click lands just after a server response, it
  // renders the stock of the PREVIOUS color and overwrites the correct
  // cross-outs. Re-apply them a fixed, small number of times after each
  // change (no observer, so this can never loop). Other apps (e.g. Selly)
  // react to class changes on the swatches, so writes are kept minimal.
  const CORRECTION_DELAYS = [60, 200];

  function scheduleCorrections(pi, names) {
    (pi.__ibkCorrectionTimers || []).forEach(clearTimeout);

    const key = names.join(' / ');
    pi.__ibkCorrectionTimers = CORRECTION_DELAYS.map(delay =>
      setTimeout(
        safe(() => {
          const current = getSelectedNames(pi);
          if (!current || current.join(' / ') !== key) return;
          updateSwatchAvailability(pi, current);
        }),
        delay
      )
    );
  }

  // Errors in this patch must never break Minimog, Swatch King, Selly or
  // linked-product-swap.js, which call into the same events.
  function safe(fn) {
    return function () {
      try {
        return fn.apply(this, arguments);
      } catch (error) {
        console.warn('[IBKÜL variant speed]', error);
      }
    };
  }

  /* ---------- Final check after the server response ---------- */

  // After Minimog applies the response for the LAST selected variant, cross
  // out sizes again from that variant: once right away and once after
  // Swatch King / linked-product-swap.js have finished re-rendering. With
  // fast clicks, those scripts can otherwise leave the cross-outs of an
  // earlier color behind. Skipped when the selection changed again since.
  function onVariantChange({ data } = {}) {
    if (!data || !data.variant || !Array.isArray(data.variant.options)) return;

    const pi = Array.from(document.querySelectorAll('product-info')).find(
      el => el.sectionId === data.sectionId && el.isConnected
    );
    if (!pi) return;

    refreshAvailability(pi, data.html);

    const names = data.variant.options.map(normalize);
    if (!isOwnSelection(pi, names)) return;

    const apply = () => {
      const current = getSelectedNames(pi);
      if (!current || current.join(' / ') !== names.join(' / ')) return;
      updateSwatchAvailability(pi, names);
    };

    apply();
    clearTimeout(pi.__ibkSettleTimer);
    pi.__ibkSettleTimer = setTimeout(safe(apply), 350);
  }

  /* ---------- 2 & 4. Instant update on option change ---------- */

  function onOptionChange({ data } = {}) {
    const source = data && data.event && data.event.target;
    if (!(source instanceof Element)) return;

    const pi = source.closest('product-info');
    if (!pi || !pi.updateMedia) return;

    // Linked product (switching to another product): leave it to Minimog.
    const productUrl = data.target && data.target.dataset && data.target.dataset.productUrl;
    if (productUrl && productUrl !== pi.dataset.url) return;

    const picker = source.closest('variant-picker') || pi.variantPicker;
    const names = getSelectedNames(pi, null, picker);

    // Linked product active (see isOwnSelection): do nothing.
    if (!isOwnSelection(pi, names)) return;

    const variant = findVariant(pi, names);
    const strings = window.MinimogStrings || {};

    updateSwatchAvailability(pi, names);
    scheduleCorrections(pi, names);

    if (!variant) {
      // Combination does not exist: lock Add to Cart until the server
      // responds, so the previous variant cannot be added to the cart.
      if (names && getVariants(pi).length) {
        pi.toggleAddButton?.(true, strings.unavailable);
      }
      return;
    }

    // Update the Add to Cart variant ID right away, without waiting for the
    // server. Otherwise a quick Add to Cart click adds the previous variant.
    pi.updateVariantInputs?.(variant.id);

    if (typeof variant.available === 'boolean') {
      pi.toggleAddButton?.(!variant.available, strings.soldOut);
    }

    if (pi.enableVariantGroupImages) pi.updateMedia({ id: variant.id });
  }

  /* ---------- Hover / touch prefetch ---------- */

  let hoverTimer = null;

  function warmUp(li) {
    const pi = li.closest('product-info');
    const option = li.closest('[id^="swatch-option"]');
    if (!pi || !option || li.getAttribute('aria-checked') === 'true') return;
    if (li.closest('.tdf_rec')) return;

    const index = parseInt(option.id.replace('swatch-option', ''), 10) - 1;
    const value = li.getAttribute('orig-value');
    if (!(index >= 0) || !value) return;

    const override = { index, value };
    const names = getSelectedNames(pi, override);
    if (!isOwnSelection(pi, names)) return;

    prefetch(buildSectionUrl(pi, override));

    const variantId = findVariantId(pi, names);
    if (variantId) preloadFirstImage(pi, variantId);
  }

  function initHover() {
    const safeWarmUp = safe(warmUp);

    document.addEventListener('pointerover', event => {
      const li = event.target instanceof Element && event.target.closest(SWATCH_SELECTOR);
      if (!li) return;
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => safeWarmUp(li), HOVER_DELAY);
    }, { passive: true });

    document.addEventListener('pointerout', event => {
      if (event.target instanceof Element && event.target.closest(SWATCH_SELECTOR)) {
        clearTimeout(hoverTimer);
      }
    }, { passive: true });

    document.addEventListener('touchstart', event => {
      const li = event.target instanceof Element && event.target.closest(SWATCH_SELECTOR);
      if (li) safeWarmUp(li);
    }, { passive: true });
  }

  /* ---------- Start ---------- */

  function start() {
    if (!window.MinimogEvents || !window.MinimogTheme) return;

    customElements.whenDefined('product-info').then(safe(() => {
      patch(customElements.get('product-info'));
    }));

    MinimogEvents.subscribe(
      MinimogTheme.pubSubEvents.optionValueSelectionChange,
      safe(onOptionChange)
    );

    if (MinimogTheme.pubSubEvents.variantChange) {
      MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.variantChange,
        safe(onVariantChange)
      );
    }

    initHover();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
